import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface CityEntry { name: string }

export function RegistrationForm() {
  const [form, setForm] = useState({
    name: "",
    age: "",
    school_name: "",
    student_class: "",
    address: "",
    city: "",
    phone: "",
  });
  const [cityData, setCityData] = useState<CityEntry[]>([]);
  const [customCity, setCustomCity] = useState("");
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [showClosedDialog, setShowClosedDialog] = useState(false);

  useEffect(() => {
    fetch("/api/cities")
      .then(r => r.json())
      .then(data => { setCityData(data); setCitiesLoading(false); })
      .catch(() => setCitiesLoading(false));

    fetch("/api/registration-status")
      .then(r => r.json())
      .then(data => { setRegistrationOpen(data.open); setStatusLoaded(true); })
      .catch(() => setStatusLoaded(true));
  }, []);

  const uniqueCities = [...new Set(cityData.map(c => c.name))].sort();
  const isOther = form.city === "Other";

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors(prev => { const next = { ...prev }; delete next[e.target.name]; return next; });
  }

  function handleImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      if (preview) URL.revokeObjectURL(preview);
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
    setErrors(prev => { const next = { ...prev }; delete next.image; return next; });
  }

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    const name = form.name.trim();
    if (!name) errs.name = "Name is required";
    else if (name.length < 2) errs.name = "Name must be at least 2 characters";
    else if (name.length > 60) errs.name = "Name must be at most 60 characters";
    else if (/[<>&"]/.test(name)) errs.name = "Name contains invalid characters";

    if (!form.age) errs.age = "Age is required";
    else {
      const ageNum = parseInt(form.age, 10);
      if (isNaN(ageNum) || ageNum < 5 || ageNum > 15) errs.age = "Age must be between 5 and 15";
    }

    const school = form.school_name.trim();
    if (!school) errs.school_name = "School name is required";
    else if (school.length < 2) errs.school_name = "School name must be at least 2 characters";
    else if (school.length > 60) errs.school_name = "School name must be at most 60 characters";
    else if (/[<>&"]/.test(school)) errs.school_name = "School name contains invalid characters";

    if (!form.student_class) errs.student_class = "Class is required";
    else {
      const cls = parseInt(form.student_class, 10);
      if (isNaN(cls) || cls < 1 || cls > 10) errs.student_class = "Class must be between 1 and 10";
    }

    const addr = form.address.trim();
    if (!addr) errs.address = "Address is required";
    else if (addr.length < 5) errs.address = "Address must be at least 5 characters";
    else if (addr.length > 100) errs.address = "Address must be at most 100 characters";

    if (!form.city) errs.city = "Ward is required";

    if (!form.phone.trim()) errs.phone = "Phone number is required";
    else if (!/^[6-9]\d{9}$/.test(form.phone)) errs.phone = "Enter a valid 10-digit phone number";

    if (image && image.size > 5 * 1024 * 1024) errs.image = "Image must be at most 5MB";
    if (image && !["image/jpeg", "image/png", "image/webp"].includes(image.type)) errs.image = "Image must be JPEG, PNG, or WebP";

    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    setError("");

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("age", form.age);
      fd.append("school_name", form.school_name);
      fd.append("student_class", form.student_class);
      fd.append("address", form.address);
      fd.append("city", isOther ? customCity.trim() : form.city);
      fd.append("phone", form.phone);
      if (image) fd.append("profile_image", image);

      const res = await fetch("/api/register", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Registration failed");
      }

      setForm({ name: "", age: "", school_name: "", student_class: "", address: "", city: "", phone: "" });
      setCustomCity("");
      setImage(null);
      setPreview(null);

      if (data.closed) {
        setShowClosedDialog(true);
      } else {
        setSuccess(true);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-[#985ae6]/20 animate-bounce-in">
        <div className="text-7xl mb-4 animate-float">⚽</div>
        <h3 className="text-2xl font-bold text-[#2D1B69] mb-2">Registration Successful! 🎉</h3>
        <p className="text-[#5716a8]/80 mb-2">Thank you for registering! Stay tuned for the winner announcement.</p>
        <p className="text-sm text-[#985ae6]/60 mb-6">Best of luck! 🤞</p>
        <button onClick={() => setSuccess(false)}
          className="inline-flex items-center gap-2 bg-[#5716a8] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-[#6B2FC9] transition-colors shadow-md">
          ⚽ Register Another Person
        </button>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-5 border border-[#985ae6]/20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Enter your full name"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow placeholder:text-gray-400" />
          {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
          <select name="age" value={form.age} onChange={handleChange} required
            className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow appearance-none bg-white cursor-pointer ${form.age ? "text-gray-900" : "text-gray-400"}`}>
            <option value="">Select age</option>
            {Array.from({ length: 11 }, (_, i) => i + 5).map(a => (
              <option key={a} value={a}>{a} years</option>
            ))}
          </select>
          {errors.age && <p className="text-red-600 text-xs mt-1">{errors.age}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
          <select name="student_class" value={form.student_class} onChange={handleChange} required
            className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow appearance-none bg-white cursor-pointer ${form.student_class ? "text-gray-900" : "text-gray-400"}`}>
            <option value="">Select class</option>
            {Array.from({ length: 10 }, (_, i) => i + 1).map(c => (
              <option key={c} value={c}>Class {c}</option>
            ))}
          </select>
          {errors.student_class && <p className="text-red-600 text-xs mt-1">{errors.student_class}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
          <input type="text" name="school_name" value={form.school_name} onChange={handleChange} required placeholder="Enter your school name"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow placeholder:text-gray-400" />
          {errors.school_name && <p className="text-red-600 text-xs mt-1">{errors.school_name}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea name="address" value={form.address} onChange={handleChange} required placeholder="Enter your address" rows={2}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow placeholder:text-gray-400 resize-none" />
          {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ward / Locality</label>
          <div className="relative">
            <select value={isOther ? "Other" : form.city} onChange={handleChange} name="city" required
              disabled={citiesLoading}
              className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow appearance-none bg-white cursor-pointer disabled:opacity-50 ${form.city ? "text-gray-900" : "text-gray-400"}`}>
              <option value="">{citiesLoading ? "Loading wards..." : "Select your ward/locality"}</option>
              {uniqueCities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {isOther && (
            <input type="text" name="customCity" value={customCity} onChange={e => setCustomCity(e.target.value)}
              placeholder="Type your ward/locality name" required
              className="mt-2 w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow placeholder:text-gray-400" />
          )}
          {errors.city && <p className="text-red-600 text-xs mt-1">{errors.city}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="e.g. 9876543210"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#985ae6] focus:border-[#985ae6] outline-none transition-shadow placeholder:text-gray-400" />
          {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Profile Image</label>
          <input type="file" accept="image/*" onChange={handleImage}
            className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#f0e8ff] file:text-[#5716a8] hover:file:bg-[#e0d0ff] cursor-pointer transition" />
          <p className="text-xs text-gray-400 mt-1">Upload a passport-size photo for better results</p>
          {errors.image && <p className="text-red-600 text-xs mt-1">{errors.image}</p>}
          {preview && (
            <img src={preview} alt="Preview" className="mt-3 h-24 w-24 rounded-lg object-cover border border-[#985ae6]/30 shadow-sm" />
          )}
        </div>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      <button type="submit" disabled={submitting}
        className="w-full bg-gradient-to-r from-[#5716a8] to-[#985ae6] text-white py-3 rounded-xl font-semibold hover:from-[#6B2FC9] hover:to-[#B07BEE] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2">
        {submitting ? (
          <>⏳ Submitting...</>
        ) : (
          <>⚽ Register Now</>
        )}
      </button>

      {showClosedDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowClosedDialog(false)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-900 mb-6">Registration is Currently Closed</h3>
            <button onClick={() => setShowClosedDialog(false)}
              className="px-6 py-2.5 bg-[#5716a8] text-white rounded-full font-semibold hover:bg-[#6B2FC9] transition-colors">
              OK
            </button>
          </div>
        </div>
      )}
    </form>
  );

  if (statusLoaded && !registrationOpen) {
    return (
      <div className="relative">
        <div className="blur-sm pointer-events-none select-none">
          {formContent}
        </div>
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-xl border border-[#985ae6]/20">
            <div className="text-5xl mb-4">🔒</div>
            <h3 className="text-xl font-bold text-[#5716a8] mb-2">Registration Closed</h3>
            <p className="text-sm text-gray-500 mb-6">Thank you for your interest!</p>
            <Link to="/leaderboard"
              className="inline-flex items-center gap-2 bg-[#5716a8] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-[#6B2FC9] transition-colors shadow-md">
              View Winners 🏆
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return formContent;
}
